import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
const PromotionalHome = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const totalSlides = 2; // Update this if you add more slides in the future

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add CSS styles for fade-in animation and sliding gallery
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .fade-in-section {
        opacity: 0;
        transform: translateY(32px);
        transition: opacity 0.7s ease-out, transform 0.7s ease-out;
      }
      
      .fade-in-section.fade-in-visible {
        opacity: 1;
        transform: translateY(0);
      }

      .slide-container {
        display: flex;
        transition: transform 0.5s ease-in-out;
        width: 100%;
      }

      .slide {
        width: 100vw;
        min-width: 100vw;
        flex-shrink: 0;
        flex-basis: 100vw;
        box-sizing: border-box;
      }

      .slide-indicator {
        transition: all 0.5s ease;
      }

      .slide-indicator.active {
        background-color: #3b82f6;
        transform: scale(1.2);
      }

      .feature-intro-card {
        position: relative;
        overflow: hidden;
      }

      .feature-intro-copy {
        position: relative;
        z-index: 1;
        max-width: min(28rem, calc(100% - 9rem));
      }

      .feature-intro-icon-wrap {
        position: absolute;
        right: 1.5rem;
        top: 50%;
        transform: translate(0, -50%) scale(0.68);
        opacity: 0.24;
        transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        pointer-events: none;
        z-index: 0;
      }

      .feature-intro-card:hover .feature-intro-icon-wrap {
        opacity: 0.34;
        transform: translate(0, -50%) scale(0.74);
      }

      @media (max-width: 640px) {
        .feature-intro-copy {
          max-width: calc(100% - 6.25rem);
        }

        .feature-intro-icon-wrap {
          right: 1rem;
          transform: translate(0, -50%) scale(0.52);
        }

        .feature-intro-card:hover .feature-intro-icon-wrap {
          transform: translate(0, -50%) scale(0.58);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Intersection Observer for fade-in animation
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('.fade-in-section');
    sections.forEach(section => observer.observe(section));

    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % 2);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + 2) % 2);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section with Sliding Gallery */}
      <div className="relative min-h-screen min-h-[100dvh] overflow-x-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>

        {/* Sliding Gallery Container */}
        <div className="relative z-10 min-h-[100dvh] h-[100dvh] max-h-screen flex flex-col">
          <div
            className="slide-container flex-1 min-h-0"
            style={{
              transform: `translateX(-${currentSlide * windowWidth}px)`,
              width: `${totalSlides * windowWidth}px`
            }}
          >
            {/* Slide 1: Original Hero Content */}
            <div className="slide flex items-start sm:items-center justify-center min-h-full py-6 sm:py-10 md:py-12">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full fade-in-section flex flex-col items-center text-left sm:text-center">
                <div className="mb-4 sm:mb-6 shrink-0">
                  <div className="inline-flex items-center justify-center w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full shadow-xl sm:shadow-2xl mx-auto sm:mx-0">
                    <img
                      src="/asset/icons/ico/logo-mono.ico"
                      alt="EDVise Logo"
                      className="w-9 h-9 sm:w-12 sm:h-12 sm:ml-1"
                    />
                  </div>
                </div>

                <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold mb-4 sm:mb-6 leading-tight max-w-4xl mx-auto">
                  <span className="block text-blue-900">
                    {t('home.hero.title')}
                  </span>
                  <span className="block text-2xl sm:text-4xl md:text-6xl text-slate-800 mt-1 sm:mt-2">
                    {t('home.hero.brand')}
                  </span>
                </h1>

                <p className="text-base sm:text-lg md:text-xl text-gray-800 max-w-2xl mx-auto mb-4 sm:mb-8 leading-relaxed">
                  {t('home.hero.subtitleShort')}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center w-full max-w-xl mx-auto">
                  <Link
                    to="/register"
                    className="group relative text-center px-6 py-3.5 sm:px-8 sm:py-4 bg-[#29496a] hover:bg-[#1F3950] text-white font-bold text-base sm:text-lg rounded-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 overflow-hidden"
                  >
                    <span className="relative z-10">{t('common.getStarted')}</span>
                  </Link>

                  <Link
                    to="/tutorials"
                    className="text-center px-6 py-3.5 sm:px-8 sm:py-4 border-2 border-gray-400 text-gray-900 font-semibold text-base sm:text-lg rounded-lg hover:border-blue-600 hover:text-blue-800 transition-all duration-300 hover:shadow-md bg-white/80"
                  >
                    {t('common.learnMore')}
                  </Link>
                </div>
              </div>
            </div>

            {/* Slide 2: Survey Content */}
            <div className="slide flex items-start sm:items-center justify-center min-h-full py-6 sm:py-10 md:py-12">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full fade-in-section flex flex-col items-center text-left sm:text-center">
                <div className="mb-3 sm:mb-5 shrink-0">
                  <div className="inline-flex items-center justify-center w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-r from-green-600 to-blue-600 rounded-full shadow-xl mx-auto sm:mx-0">
                    <svg className="w-9 h-9 sm:w-12 sm:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>

                <h1 className="text-2xl sm:text-4xl md:text-6xl font-extrabold mb-4 sm:mb-6 leading-tight max-w-4xl mx-auto">
                  <span className="block text-emerald-900">
                    {t('home.survey.title')}
                  </span>
                </h1>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center w-full max-w-xl mx-auto mb-5 sm:mb-6">
                  <Link
                    to="/jupasurvey"
                    className="group relative text-center px-6 py-3.5 sm:px-8 sm:py-4 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-base sm:text-lg rounded-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
                  >
                    <span className="relative z-10">{t('home.survey.surveyButton')}</span>
                  </Link>

                  <Link
                    to="/jupas-calculator"
                    className="text-center px-6 py-3.5 sm:px-8 sm:py-4 border-2 border-gray-400 text-gray-900 font-semibold text-base sm:text-lg rounded-lg hover:border-emerald-600 hover:text-emerald-900 transition-all duration-300 hover:shadow-md bg-white/80"
                  >
                    {t('home.survey.jupaselectButton')}
                  </Link>
                </div>

                <p className="text-gray-800 text-sm sm:text-base font-medium mb-3 max-w-2xl mx-auto">
                  {t('home.survey.lead')}
                </p>
                <ul className="text-gray-700 text-sm sm:text-base space-y-2 max-w-2xl mx-auto mb-2 text-left list-disc pl-5 sm:pl-5 sm:mx-auto w-full">
                  <li>{t('home.survey.bullet1')}</li>
                  <li>{t('home.survey.bullet2')}</li>
                  <li>{t('home.survey.bullet3')}</li>
                </ul>
                <p className="hidden lg:block text-gray-600 text-base mt-4 max-w-4xl mx-auto leading-relaxed">
                  {t('home.survey.description')}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Arrows — low on small screens to avoid overlapping body text */}
          {currentSlide > 0 && (
            <button
              type="button"
              onClick={prevSlide}
              aria-label={t('home.carousel.prev')}
              className="absolute left-2 bottom-24 md:left-4 md:bottom-auto md:top-1/2 md:-translate-y-1/2 z-20 p-2.5 md:p-3 bg-white/90 hover:bg-white text-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200/80"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {currentSlide < totalSlides - 1 && (
            <button
              type="button"
              onClick={nextSlide}
              aria-label={t('home.carousel.next')}
              className="absolute right-2 bottom-24 md:right-4 md:bottom-auto md:top-1/2 md:-translate-y-1/2 z-20 p-2.5 md:p-3 bg-white/90 hover:bg-white text-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200/80"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Slide Indicators */}
          <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3 items-center">
            {[0, 1].map((index) => (
              <button
                key={index}
                type="button"
                onClick={() => goToSlide(index)}
                aria-label={t('home.carousel.goTo', { n: index + 1, total: totalSlides })}
                aria-current={currentSlide === index ? 'true' : undefined}
                className={`slide-indicator w-3 h-3 rounded-full transition-all duration-300 ${currentSlide === index ? 'active bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
              />
            ))}
          </div>
        </div>

        {/* Floating Elements — inset so hero logo is not clipped */}
        <div className="pointer-events-none absolute top-28 left-4 sm:left-10 w-16 h-16 sm:w-20 sm:h-20 bg-blue-400/20 rounded-full blur-xl animate-pulse" />
        <div className="pointer-events-none absolute bottom-24 right-4 sm:right-10 w-24 h-24 sm:w-32 sm:h-32 bg-purple-400/20 rounded-full blur-xl animate-pulse delay-1000" />
        <div className="pointer-events-none absolute top-1/2 left-8 sm:left-20 w-12 h-12 sm:w-16 sm:h-16 bg-pink-400/20 rounded-full blur-xl animate-pulse delay-500" />
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white relative fade-in-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('home.features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* DSE Analysis */}
            <Link
              to="/jupas-calculator"
              className="feature-intro-card bg-gradient-to-br from-indigo-50 to-indigo-100 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 block h-full"
            >
              <div className="feature-intro-copy text-justify">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.features.dseAnalysis.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.dseAnalysis.description')}</p>
              </div>
              <div className="feature-intro-icon-wrap" aria-hidden>
                <div className="flex h-32 w-32 items-center justify-center rounded-3xl bg-gradient-to-br from-indigo-500/90 to-indigo-600/90 shadow-inner">
                  <svg className="h-20 w-20 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </Link>

            {/* JUPAS Selection */}
            <Link
              to={isAuthenticated ? '/' : '/login'}
              className="feature-intro-card bg-gradient-to-br from-red-50 to-red-100 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 block"
            >
              <div className="feature-intro-copy text-justify">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.features.jupaSelection.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.jupaSelection.description')}</p>
              </div>
              <div className="feature-intro-icon-wrap" aria-hidden>
                <div className="flex h-32 w-32 items-center justify-center rounded-3xl bg-gradient-to-br from-red-500/90 to-red-600/90 shadow-inner">
                  <svg className="h-20 w-20 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
              </div>
            </Link>

            {/* Coming Soon

            {/* Personality Assessment
            <div className="group bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.features.personalityAssessment.title')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('home.features.personalityAssessment.description')}</p>
            </div>

            {/* AI Guidance
            <div className="group bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.features.aiGuidance.title')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('home.features.aiGuidance.description')}</p>
            </div>

            {/* Academic Trajectory
            <div className="group bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.features.academicTrajectory.title')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('home.features.academicTrajectory.description')}</p>
            </div>

            {/* Subject Recommendations
            <div className="group bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.features.subjectRecommendations.title')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('home.features.subjectRecommendations.description')}</p>
            </div>
            */}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 overflow-hidden fade-in-section">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
            <span className="block">{t('home.cta.title')}</span>
            <span className="block text-2xl md:text-3xl text-blue-100 mt-4">{t('home.cta.subtitle')}</span>
          </h2>

          <Link
            to="/register"
            className="inline-flex items-center px-10 py-5 bg-white text-[#29496a] font-bold text-xl rounded-lg shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 group"
          >
            <span>{t('common.getStarted')}</span>
            <svg className="w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
      </div>
    </div>
  );
};

export default PromotionalHome; 
